
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator"
import { TemplateDetails } from "@/types"
import { TemplateTreeNode } from "@/types/treeNode"
import TreeTemplates from "../Tree/TreeTemplates.vue"
import api from "@/services/api"

@Component({
	components: {
		"tree-templates": TreeTemplates
	}
})
export default class TemplateInheritanceChain extends Vue {
	/* --- Refs --- */
	@Ref() treeTemplates!: TreeTemplates

	/* --- Props --- */

	@Prop({ type: Object, required: true })
	private rootTemplate!: TemplateDetails

	@Prop({ type: Object, required: false, default: () => ({}) })
	private affected?: { fieldIds?: Set<string>; sectionIds?: Set<string> }

	@Prop({ type: Boolean, default: false })
	private showOverridesOnly!: boolean

	/* --- Data --- */

	private static readonly ContractByDefaultAboveThreshold: number = 5

	private loading: boolean = false
	private treeNode: TemplateTreeNode | null = null
	private chainTemplates: TemplateDetails[] = []

	/* --- Watchers --- */

	@Watch("rootTemplate", { immediate: true, deep: true })
	private onRootTemplateChanged (newValue: TemplateDetails, oldValue: TemplateDetails): void {
		if (newValue?.id === oldValue?.id) {
			return
		}

		this.rebuildTree()
	}

	/* --- Lifecycle Hooks --- */

	async mounted (): Promise<void> {
	}

	/* --- Methods --- */

	private async rebuildTree (): Promise<void> {
		this.loading = true
		try {
			var childrenMapObj: Record<string, TemplateDetails[]> = {}

			if (this.rootTemplate.id) {
				// Fetch the full inheritance chain from the API
				this.chainTemplates = await api.templateInheritanceChain(this.rootTemplate.id!)
				childrenMapObj = this.buildChildrenMap(this.chainTemplates)
			}

			const childrenMap = (id: string) =>
				(childrenMapObj[id] as TemplateDetails[] | undefined) || []

			this.treeNode = this.buildSubtree(this.rootTemplate, childrenMap)

			if (this.treeNode) {
				this.computeOverridesInSubtree(this.treeNode)
			}
		} finally {
			this.loading = false
		}
	}

	/* --- Methods --- */

	/**
	 * Builds a mapping of parent template IDs to their direct child templates by grouping the
	 * provided templates by their "inheritedFrom" property, enabling quick lookup of all templates
	 * that inherit from a given parent.
	 *
	 * Example:
	 *   T2.inheritedFrom = "T1"
	 *   T3.inheritedFrom = "T1"
	 *   T4.inheritedFrom = "T2"
	 *
	 * Result:
	 * {
	 *   "T1": [T2, T3],
	 *   "T2": [T4]
	 * }
	 *
	 * @param templates - The list of templates to process.
	 * @returns A dictionary keyed by parent template ID, where each value is an array of child templates.
	 */
	private buildChildrenMap (templates: TemplateDetails[]): Record<string, TemplateDetails[]> {
		const childrenMap: Record<string, TemplateDetails[]> = {}

		for (const template of templates) {
			if (template.inheritedFrom) {
				if (!childrenMap[template.inheritedFrom]) {
					childrenMap[template.inheritedFrom] = []
				}
				childrenMap[template.inheritedFrom].push(template)
			}
		}

		return childrenMap
	}

	/**
	 * Recursively computes which fields and sections are affected by overrides
	 * for each node in the template inheritance tree.
	 *
	 * For each node, it collects the set of field and section IDs
	 * that are overridden in the node itself or in any of its descendants.
	 *
	 * - If a field or section is overridden anywhere in a node's subtree (including itself),
	 *   it will be included in that node's "visibleFields" and "visibleSections" sets.
	 * - This ensures that a parent node will show all fields and sections that are affected
	 *   by overrides lower down in the chain, as well as its own.
	 * - The computed sets are attached to each node as "visibleFields" and "visibleSections"
	 *   for use in rendering (e.g., by TreeTemplates).
	 *
	 * @param node - The current tree node being processed.
	 * @returns An object containing the sets of field and section IDs overridden in this node or any descendant.
	 */
	private computeOverridesInSubtree (
		node: TemplateTreeNode,
		inheritedFields: Set<string> = new Set(),
		inheritedSections: Set<string> = new Set()
	): { fields: Set<string>; sections: Set<string> } {
		// Start with inherited overrides
		const fields = new Set(inheritedFields)
		const sections = new Set(inheritedSections)

		// Add this node's own overrides
		const fieldOverrides = node.node.inheritanceOverrides?.fields
		if (fieldOverrides) {
			for (const fieldId in fieldOverrides) {
				const values = fieldOverrides[fieldId]
				if (Array.isArray(values) && values.length > 0) {
					fields.add(fieldId)
				}
			}
		}

		const sectionOverrides = node.node.inheritanceOverrides?.sections
		if (sectionOverrides) {
			for (const sectionId in sectionOverrides) {
				const values = sectionOverrides[sectionId]
				if (Array.isArray(values) && values.length > 0) {
					sections.add(sectionId)
				}
			}
		}

		// Merge down to children
		if (node.children) {
			for (const child of node.children) {
				var childOverrides = this.computeOverridesInSubtree(child, fields, sections)
				childOverrides.fields.forEach(f => fields.add(f))
				childOverrides.sections.forEach(f => sections.add(f))
			}
		}

		// Attach to node for use in TreeTemplates
		node.visibleFields = new Set(fields)
		node.visibleSections = new Set(sections)

		return { fields, sections }
	}

	/**
	 * Recursively constructs the template inheritance tree starting from the given root template.
	 *
	 * For each template, this method creates a tree node containing:
	 *   - The template details ("node")
	 *   - An array of child nodes ("children"), built by recursively processing all templates that inherit from this template
	 *   - A "visible" flag (default: true)
	 *
	 * The "childrenMap" function provides a lookup for direct child templates by parent template ID.
	 *
	 * @param root - The root template for the current subtree.
	 * @param childrenMap - A function that returns all child templates for a given template ID.
	 * @returns The constructed TemplateTreeNode representing the root and its descendants.
	 */
	private buildSubtree (
		root: TemplateDetails,
		childrenMap: (id: string) => TemplateDetails[]
	): TemplateTreeNode {
		const childrenTemplates = childrenMap(root.id!) || []
		const children = childrenTemplates.length
			? childrenTemplates.map(child =>
				this.buildSubtree(child, childrenMap)
			)
			: null

		return {
			node: root,
			children,
			visible: true
		}
	}

	public expandAll (): void {
		this.treeTemplates.expandAll()
	}

	public collapseAll (childrenOnly: boolean = false): void {
		this.treeTemplates.collapseAll(childrenOnly)
	}

	/**
	 * Sets the expanded/collapsed state of the tree back to default.
	 */
	public setDefaultExpandedState (): void {
		if (!this.shouldCollapseByDefault) {
			this.expandAll()
		} else {
			this.collapseAll(true)
		}
	}

	private get shouldCollapseByDefault (): boolean {
		if (this.showOverridesOnly) {
			return false
		} else {
			return !!this.treeNode?.children &&
				this.treeNode.children.length >= TemplateInheritanceChain.ContractByDefaultAboveThreshold
		}
	}
}
